import type { Reading } from '@casa/driver-sdk';
import type { Graph, GraphFill, Threshold } from '@casa/model';
import { cumulativePoints, type GraphPoint } from '../../elements/graph.ts';
import { readingPoints } from '../../values/readings.ts';

/**
 * The `graph` tile (docs/specs/graph-tile.md): its series from the readings, laid over the latest
 * where they lie before it in time, and the time axis' ticks. Chart.js draws it (`GraphTileView`).
 */

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** Round steps for the time axis, from five minutes to a week (GR-1). */
const TIME_STEPS = [5 * MINUTE, 15 * MINUTE, 30 * MINUTE, HOUR, 2 * HOUR, 3 * HOUR, 6 * HOUR, 12 * HOUR, DAY, 2 * DAY, 7 * DAY];
const LONGEST_STEP = 7 * DAY;
/** The width a time label takes with room beside it, in CSS pixels. */
const TICK_ROOM = 60;

export interface GraphSeries {
    points: readonly GraphPoint[];
    color: string;
    /** Device pixels, as in the legacy view. */
    lineWidth: number;
    label: string | undefined;
    fill: GraphFill;
    /** A bar's colour from its value; the series' colour below the first. */
    thresholds: readonly Threshold[];
    /** Added to each point's time where it is drawn: whole days, for a series laid over the latest (GR-12). */
    shift: number;
}

/** A bar's colour: that of the highest threshold its value has reached, else the series' own. */
export function barColor(value: number, color: string, thresholds: readonly Threshold[]) : string {
    let found: Threshold | undefined;

    for (const threshold of thresholds) {
        if (value >= threshold.from && (found === undefined || threshold.from >= found.from)) {
            found = threshold;
        }
    }

    return found?.color ?? color;
}

/** One series per graph series that has readings, in the order of the options; `readings` follows that order. */
export function graphSeries(graph: Graph, readings: readonly (Reading[] | null | undefined)[]) : GraphSeries[] {
    const series: GraphSeries[] = [];

    for (const [index, options] of graph.series.entries()) {
        const points = readingPoints(readings[index]);

        if (points.length === 0) {
            continue;
        }

        series.push({
            points: options.cumulative ? cumulativePoints(points) : points,
            color: options.color,
            lineWidth: graph.lineWidth,
            label: options.label?.trim() || undefined,
            fill: options.fill,
            thresholds: options.thresholds,
            shift: 0,
        });
    }

    return overlaid(series);
}

/**
 * A series that ends before the latest one starts ("yesterday" beside "today") is moved forward by
 * whole days, at least one, onto it, so that the same time of day is drawn at the same place (GR-12).
 */
function overlaid(series: GraphSeries[]) : GraphSeries[] {
    const latest = series.reduce<GraphSeries | undefined>((found, each) => (!found || lastTime(each) > lastTime(found) ? each : found), undefined);

    if (!latest) {
        return series;
    }

    const start = firstTime(latest);

    return series.map((each) => {
        if (lastTime(each) >= start) {
            return each;
        }

        return { ...each, shift: Math.max(1, Math.round((start - firstTime(each)) / DAY)) * DAY };
    });
}

const firstTime = (series: GraphSeries) : number => series.points.reduce((first, point) => Math.min(first, point.time), Infinity);
const lastTime = (series: GraphSeries) : number => series.points.reduce((last, point) => Math.max(last, point.time), -Infinity);

/**
 * The time axis' ticks between `min` and `max` on an axis `width` CSS pixels wide: the first round
 * step that leaves room for each label (and gives at least three where it can), at whole steps in
 * the device's time zone, so that hours fall on the hour and days at midnight.
 */
export function timeTicks(min: number, max: number, width: number) : { step: number; ticks: number[] } {
    const most = Math.max(3, Math.floor(width / TICK_ROOM));
    const step = TIME_STEPS.find((each) => Math.floor((max - min) / each) + 1 <= most) ?? LONGEST_STEP;
    const ticks: number[] = [];
    // Steps of an hour or less fall on the hour in every time zone; longer ones are counted from local midnight.
    const offset = step > HOUR ? -new Date(min).getTimezoneOffset() * MINUTE : 0;
    
    for (let local = Math.ceil((min + offset) / step) * step; local - offset <= max; local += step) {
        ticks.push(local - offset);
    }

    return { step, ticks };
}

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatterFor(options: Intl.DateTimeFormatOptions) : Intl.DateTimeFormat {
    const key = JSON.stringify(options);
    let formatter = formatters.get(key);
    if (!formatter) {
        // The app is in English (weather spec WT-6): the names of days are English, times are 24-hour.
        formatter = new Intl.DateTimeFormat('en-GB', options);
        formatters.set(key, formatter);
    }

    return formatter;
}

/** A time as the axis says it at `step`: "14:00", or "Sat 24" for steps of a day and more. */
export function timeLabel(time: number, step: number) : string {
    return step >= DAY
        ? formatterFor({ weekday: 'short', day: 'numeric' }).format(time)
        : formatterFor({ hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(time);
}

/** A tap's time (GR-7) on a graph spanning `span`: "14:05", with the day ("Sat 24 14:05") from a day on. */
export function tapTimeLabel(time: number, span: number) : string {
    return span >= DAY ? `${timeLabel(time, DAY)} ${timeLabel(time, HOUR)}` : timeLabel(time, HOUR);
}
